import React, { useEffect, useState } from 'react';
import { toast } from '@ohif/ui-next';
import { DicomMetadataStore } from '@ohif/core';
import { apiUrl } from '../../UploadDocument/apiUrl';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';

interface DicomSenderProps {
  seriesNumberCont: number;
}

const SendDicomSerie: React.FC<DicomSenderProps> = ({ seriesNumberCont }) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const [dicomServerIp, setDicomServerIp] = useState('');
  const [dicomServerPort, setDicomServerPort] = useState<number>(11112);
  const [dicomAETitle, setDicomAETitle] = useState('');
  const [callingAETitle, setCallingAETitle] = useState('');

  const failedUrls = new Set<string>();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const studyMetadata = queryParams.get('StudyInstanceUIDs');
  const [servers, setServers] = useState<any[]>([]); // Array de servidores
  const [selectedServer, setSelectedServer] = useState<string>(''); // Id del servidor seleccionado

  // Cargar los servidores disponibles desde la API
  useEffect(() => {
    const fetchServers = async () => {
      try {
        const response = await fetch(apiUrl + '/api/server/');
        const data = await response.json();

        if (data && Array.isArray(data)) {
          setServers(data);

          // Seleccionar el servidor por defecto (si existe)
          const defaultServer = data.find((server: any) => server.isDefault);
          if (defaultServer) {
            setSelectedServer(defaultServer._id);
            setDicomServerIp(defaultServer.host);
            setDicomServerPort(defaultServer.port);
            setDicomAETitle(defaultServer.calledAET);
            setCallingAETitle(defaultServer.callingAET);
          }
        }
      } catch (error) {
        console.error('Error al obtener los servidores:', error);
        toast.error('Error al obtener los servidores');
      }
    };

    fetchServers();
  }, []);

  const sendImageToBackend = async (image: {
    url: string;
    fileName: string;
    seriesUID: string;
  }): Promise<void> => {
    const { url, fileName, seriesUID } = image;

    try {
      const response = await fetch(apiUrl + '/api/dicom/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dicomFile: url,
          host: dicomServerIp,
          port: dicomServerPort,
          calledAET: dicomAETitle,
          callingAET: callingAETitle || 'MAYU',
        }),
      });

      if (!response.ok) {
        console.error(`Error al enviar imagen ${fileName}:`, response.statusText);
        failedUrls.add(url);
      }
    } catch (error) {
      console.error(`Error enviando imagen ${fileName}:`, error);
      failedUrls.add(url);
    }
  };


  const enviarTodasLasImágenes = async () => {
    try {
      setLoading(true);
      setProgress(0);
      failedUrls.clear();

      const studyMeta = DicomMetadataStore.getStudy(studyMetadata);
      if (!studyMeta) {
        toast.error('No se encontró el estudio');
        return;
      }

      const studyUID = studyMeta.StudyInstanceUID;

      // 🔹 Agrupar imágenes por serie
      const seriesMap: Record<
        string,
        { url: string; fileName: string; seriesUID: string }[]
      > = {};

      const filteredSeries = studyMeta.series.filter(
        series => series.SeriesNumber === seriesNumberCont
      );

      if (filteredSeries.length === 0) {
        toast.error('No se encontró la serie con el número especificado');
        return;
      }

      for (const series of filteredSeries) {
        const seriesUID = series.SeriesInstanceUID;
        seriesMap[seriesUID] = [];

        for (const instance of series.instances) {
          const sopUID = instance.SOPInstanceUID;
          const rootUrl = instance.wadoRoot;

          const match = rootUrl.match(/^(https?:\/\/[^\/]+)/);
          if (match && studyUID && seriesUID && sopUID) {
            const wadoUrl =
              `${match[0]}/dcm4chee-arc/aets/LOCAL/wado` +
              `?requestType=WADO&studyUID=${studyUID}&seriesUID=${seriesUID}` +
              `&objectUID=${sopUID}&contentType=application/dicom`;

            const fileName = `DICOM_${seriesUID}_${sopUID}.dcm`;

            seriesMap[seriesUID].push({
              url: wadoUrl,
              fileName,
              seriesUID,
            });
          }
        }
      }

      const allImages = Object.values(seriesMap).flat();
      if (allImages.length === 0) {
        toast.error('No se encontraron imágenes para enviar');
        return;
      }

      const totalImages = allImages.length;
      let loaded = 0;

      toast.info(
        `Enviando ${totalImages} imágenes en ${Object.keys(seriesMap).length} series...`,
        { duration: 6000 }
      );

      // 🔹 Función que envía UNA serie completa
      const enviarSerie = async (
        images: { url: string; fileName: string; seriesUID: string }[]
      ) => {
        for (const item of images) {
          if (failedUrls.has(item.url)) continue;

          await sendImageToBackend(item);

          loaded++;
          setProgress(Math.round((loaded / totalImages) * 100));

          // Deja respirar a la UI
          await new Promise(res => setTimeout(res, 500));
        }
      };

      // 🔹 Ejecutar TODAS las series en paralelo
      const seriesPromises = Object.values(seriesMap).map(enviarSerie);

      await Promise.all(seriesPromises);

      toast.success('Todas las series se enviaron correctamente');
    } catch (err) {
      console.error('Error al enviar imágenes:', err);
      toast.error('Error al enviar las imágenes');
    } finally {
      setLoading(false);
    }
  };
  const handleStart = () => {
    if (!selectedServer) {
      toast.error('Por favor selecciona un servidor');
      return;
    }

    setShowModal(false);
    enviarTodasLasImágenes();
  };
  const handleServerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const serverId = e.target.value;
    const selected = servers.find(server => server._id === serverId);

    if (selected) {
      setSelectedServer(serverId);
      setDicomServerIp(selected.host);
      setDicomServerPort(selected.port);
      setDicomAETitle(selected.calledAET);
      setCallingAETitle(selected.callingAET);
    }
  };

  return (
    <div>
      <button
        onClick={() => setShowModal(true)}
        disabled={loading}
        // className="bg-secondary-main hover:bg-secondary-light flex w-full items-center justify-center gap-2 rounded p-2 text-white sm:justify-between"
        className={classnames(
          'bg-primary-main absolute top-12 left-0 mx-auto mt-2 flex w-full items-center justify-center gap-[2px] p-[4px]',
          'hover:bg-primary-light/30 rounded text-white'
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          className="bi bi-send-arrow-up"
          viewBox="0 0 16 16"
        >
          <path
            fillRule="evenodd"
            d="M15.854.146a.5.5 0 0 1 .11.54l-2.8 7a.5.5 0 1 1-.928-.372l1.895-4.738-7.494 7.494 1.376 2.162a.5.5 0 1 1-.844.537l-1.531-2.407L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM5.93 9.363l7.494-7.494L1.591 6.602z"
          />
          <path
            fillRule="evenodd"
            d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m.354-5.354a.5.5 0 0 0-.722.016l-1.149 1.25a.5.5 0 1 0 .737.676l.28-.305V14a.5.5 0 0 0 1 0v-1.793l.396.397a.5.5 0 0 0 .708-.708z"
          />
        </svg>
        {loading && (
          <div
            className="spinner-container"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <span
              style={{ marginLeft: '10px' }}
              className="text-sm"
            >
              {Math.round(progress)}%
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-arrow-clockwise animate-spin text-sm"
              viewBox="0 0 16 16"
            >
              <path
                fillRule="evenodd"
                d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"
              />
              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466" />
            </svg>
          </div>
        )}
        {loading ? '    ' : 'Enviar dcm'}
      </button>
      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 text-white">
          <div className="bg-secondary-dark w-full max-w-md rounded p-6 shadow-lg">
            <h2 className="mb-4 text-xl font-semibold">Configuración de Envío DICOM</h2>
            <div className="space-y-4">
              <label className="text-lg">Seleccionar Servidor DICOM</label>
              <select
                className="w-full rounded border p-2 text-black hover:bg-black hover:text-white"
                value={selectedServer}
                onChange={handleServerChange}
              >
                <option value="">Seleccione un servidor</option>
                {servers.map(server => (
                  <option
                    key={server._id}
                    value={server._id}
                  >
                    {server.name} ({server.host})
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-6 flex justify-between">
              <button
                onClick={() => setShowModal(false)}
                className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
              >
                Cancelar
              </button>
              <button
                onClick={handleStart}
                disabled={loading || !selectedServer}
                className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
              >
                {loading ? 'Enviando...' : 'Iniciar envío'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendDicomSerie;
